import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import type { MetaData } from '@mwl/core-lib';
import {
  getCategoriesSB,
  getChampionshipsSB,
  getSportsSB,
  selectUserCurrency,
  useFetch,
  useSportUrlParams,
} from '@mwl/core-lib';

import { routes } from '@/constants';
import { useTypedSelector } from '@/hooks';

interface UseSportMetaParams extends Required<Omit<MetaData, 'metaVariables'>>, Pick<MetaData, 'metaVariables'> {}

export const useSportMeta = (): UseSportMetaParams | undefined => {
  const { pathname } = useRouter();
  const currency = useTypedSelector(selectUserCurrency);

  const { sportId, sportIds, categoryId, categoryIds, championshipId, championshipIds, stage } = useSportUrlParams();

  const pageIds = useMemo(() => {
    const getIds = (ids?: 'all' | string | Array<string>): Array<string> => {
      if (!ids || ids === 'all') {
        return [];
      }

      if (typeof ids === 'string') {
        return [ids];
      }

      return ids;
    };

    return {
      sport: getIds(sportId || sportIds),
      category: getIds(categoryId || categoryIds),
      championship: getIds(championshipId || championshipIds),
    };
  }, [sportId, sportIds, categoryId, categoryIds, championshipId, championshipIds]);

  const fetchSports = useCallback(() => {
    return getSportsSB({
      sportIds: pageIds.sport,
    });
  }, [pageIds.sport]);

  const fetchCategories = useCallback(() => {
    return getCategoriesSB({
      categoryIds: pageIds.category,
    });
  }, [pageIds.category]);

  const fetchChampionships = useCallback(() => {
    return getChampionshipsSB({
      championshipIds: pageIds.championship,
    });
  }, [pageIds.championship]);

  const { data: sportList } = useFetch(`loadSports_${pageIds.sport.join('_')}`, fetchSports, {
    disabled: !pageIds.sport.length || !currency,
  });

  const { data: categoryList } = useFetch(`loadCategories_${pageIds.category.join('_')}`, fetchCategories, {
    disabled: !pageIds.category.length || !currency,
  });

  const { data: championshipList } = useFetch(
    `loadChampionships_${pageIds.championship.join('_')}`,
    fetchChampionships,
    {
      disabled: !pageIds.championship.length || !currency,
    },
  );

  const titles = useMemo(
    () => ({
      sport: sportList?.sports?.map((sport) => sport.title).join(', ') || '',
      category: categoryList?.categories?.map((category) => category.title).join(', ') || '',
      championship: championshipList?.championships?.map((championship) => championship.title).join(', ') || '',
    }),
    [categoryList?.categories, championshipList?.championships, sportList?.sports],
  );

  if (pathname !== routes.sport.home) {
    return undefined;
  }

  const livePostfix = stage === 'LIVE' ? '_live' : '';

  if (!titles.sport) {
    return {
      title: `bnz_title.sport_main${livePostfix}`,
      description: `bnz_description.sport_main${livePostfix}`,
    };
  }

  const categoryPostfix = titles.category ? '_category' : '';
  const championshipPostfix = titles.category && titles.championship ? '_championship' : '';
  const metaVariables: Record<string, string> = { sport_title: titles.sport };

  if (titles.category && titles.championship) {
    metaVariables.championship_title = titles.championship;
  } else if (titles.category) {
    metaVariables.category_title = titles.category;
  }

  return {
    title: `bnz_title.sports${categoryPostfix}${championshipPostfix}${livePostfix}`,
    description: `bnz_description.sports${categoryPostfix}${championshipPostfix}${livePostfix}`,
    metaVariables,
  };
};
